import React, { useState } from "react";
import { ChartOptions, type ChartData } from "chart.js";
import {
  DollarSign,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useFinancialOverviewQuery } from "../../api/hooks/useFinancialOverview";
import { useFinancialComparisonQuery } from "../../api/hooks/useFinancialComparison";
import { useDepartments } from "../../api/hooks/useDepartments";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { PAGE_CONTAINER_HEIGHT } from "../../constants/layout";
import {
  type PeriodType,
  formatCurrency,
  getPeriodLabel,
  type FinancialViewMode,
} from "./financial-overview/utils";
import { FinancialFilters } from "./financial-overview/FinancialFilters";
import { FinancialChart } from "./financial-overview/FinancialChart";
import { FinancialComparisonChart } from "./financial-overview/FinancialComparisonChart";
import { FinancialDataTable } from "./financial-overview/FinancialDataTable";
import { FinancialComparisonTable } from "./financial-overview/FinancialComparisonTable";
import { FinancialDataCards } from "./financial-overview/FinancialDataCards";
import { FinancialSummaryCards } from "./financial-overview/FinancialSummaryCards";
import { COMPARISON_METRIC_LABELS, getYtdForMetric, orderMetrics, type ComparisonMetric } from "./financial-overview/comparisonUtils";
import { FinancialJournalPanel } from "./financial-overview/FinancialJournalPanel";
import { getJournalPointProps, useFinancialJournalEntries } from "./financial-overview/journal";
import { buildJournalTooltipCallback } from "../charts/journalMarkers";
import type { JournalEntryDto } from "../../api/generated/api-client";
import { useScreenView } from '../../telemetry/useScreenView';

const NO_JOURNAL_ENTRIES: JournalEntryDto[] = [];
const BALANCE_LINE_COLOR = "rgb(59, 130, 246)";
const TOTAL_BALANCE_LINE_COLOR = "rgb(245, 158, 11)";

const FinancialOverview: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] =
    useState<PeriodType>("current-year");
  const [includeStockData, setIncludeStockData] = useState<boolean>(true);
  const [includeCurrentMonth, setIncludeCurrentMonth] = useState<boolean>(false);
  const [excludedDepartments, setExcludedDepartments] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<FinancialViewMode>("timeline");
  const [comparisonYears, setComparisonYears] = useState<number>(2);
  const [comparisonMetrics, setComparisonMetrics] = useState<ComparisonMetric[]>(["income", "expenses"]);
  const [comparisonRolling, setComparisonRolling] = useState<boolean>(true);
  const [isDataExpanded, setIsDataExpanded] = useState(false);
  const isMobile = useIsMobile();
  const initialDefaultsSet = React.useRef(false);

  useScreenView('Finance', 'FinancialOverview');

  const { data: departments } = useDepartments();

  React.useEffect(() => {
    if (departments && !initialDefaultsSet.current) {
      initialDefaultsSet.current = true;
      const buvol = departments.find((d) => d.name === "Buvol");
      if (buvol) {
        setExcludedDepartments([buvol.id]);
      }
    }
  }, [departments]);

  // Convert period type to months for API call.
  // For "current-year" periods, we count completed months only (now.getMonth() = 0-indexed).
  // The +1 for includeCurrentMonth is handled by the backend via the includeCurrentMonth flag,
  // but we still need to pass the correct total month count.
  const getMonthsFromPeriod = (period: PeriodType): number => {
    const now = new Date();
    const currentMonthOffset = includeCurrentMonth ? 1 : 0;
    switch (period) {
      case "current-year":
        return now.getMonth() + currentMonthOffset;
      case "current-and-previous-year":
        return now.getMonth() + currentMonthOffset + 12;
      case "last-6-months":
        return 6;
      case "last-13-months":
        return 13;
      case "last-26-months":
        return 26;
      default: {
        const _exhaustive: never = period;
        throw new Error(`Unhandled period: ${_exhaustive}`);
      }
    }
  };

  const months = getMonthsFromPeriod(selectedPeriod);
  const { data, isLoading, error, isRefetching } = useFinancialOverviewQuery(
    months,
    includeStockData,
    excludedDepartments,
    includeCurrentMonth,
  );

  const {
    data: comparisonData,
    isLoading: isComparisonLoading,
    error: comparisonError,
  } = useFinancialComparisonQuery(
    comparisonYears,
    includeStockData,
    excludedDepartments,
    includeCurrentMonth,
    viewMode === "comparison",
  );

  const sortedData = React.useMemo(
    () =>
      [...(data?.data ?? [])].sort((a, b) => {
        if (a.year !== b.year) return a.year - b.year;
        return a.month - b.month;
      }),
    [data?.data],
  );

  const chartMonths = React.useMemo(
    () => sortedData.map((item) => ({ year: item.year, month: item.month })),
    [sortedData],
  );

  const journalQuery = useFinancialJournalEntries(
    chartMonths,
    viewMode === "timeline",
  );
  const journalEntries = journalQuery.data?.entries ?? NO_JOURNAL_ENTRIES;

  const chartData = React.useMemo(() => {
    if (!data?.data) return null;

    const labels = sortedData.map((item) => item.monthYearDisplay);
    const incomeData = sortedData.map((item) => item.income);
    const expensesData = sortedData.map((item) => item.expenses);
    const balanceData = sortedData.map((item) => item.financialBalance);
    const stockChangeData = sortedData.map(
      (item) => item.totalStockValueChange || 0,
    );
    const totalBalanceData = sortedData.map(
      (item) => item.totalBalance || item.financialBalance,
    );

    const datasets: any[] = [
      {
        label: "Příjmy",
        type: "bar" as const,
        data: incomeData,
        backgroundColor: "rgba(34, 197, 94, 0.6)",
        borderColor: "rgb(34, 197, 94)",
        borderWidth: 1,
      },
      {
        label: "Náklady",
        type: "bar" as const,
        data: expensesData,
        backgroundColor: "rgba(239, 68, 68, 0.6)",
        borderColor: "rgb(239, 68, 68)",
        borderWidth: 1,
      },
      {
        label: "Účetní bilance",
        type: "line" as const,
        data: balanceData,
        borderColor: BALANCE_LINE_COLOR,
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        fill: false,
        tension: 0.1,
        borderWidth: 3,
        ...getJournalPointProps(chartMonths, journalEntries, BALANCE_LINE_COLOR),
      },
    ];

    if (includeStockData) {
      datasets.push(
        {
          label: "Změna hodnoty skladu",
          type: "bar" as const,
          data: stockChangeData,
          backgroundColor: "rgba(168, 85, 247, 0.6)",
          borderColor: "rgb(168, 85, 247)",
          borderWidth: 1,
        },
        {
          label: "Celková bilance (vč. skladu)",
          type: "line" as const,
          data: totalBalanceData,
          borderColor: TOTAL_BALANCE_LINE_COLOR,
          backgroundColor: "rgba(245, 158, 11, 0.1)",
          fill: false,
          tension: 0.1,
          borderWidth: 4,
          ...getJournalPointProps(chartMonths, journalEntries, TOTAL_BALANCE_LINE_COLOR),
        },
      );
    }

    return { labels, datasets } as ChartData<"bar">;
  }, [data?.data, sortedData, chartMonths, journalEntries, includeStockData]);

  const chartOptions: ChartOptions<"bar"> = React.useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: isMobile ? ("top" as const) : ("right" as const),
          align: "center" as const,
          labels: {
            boxWidth: 12,
            padding: isMobile ? 5 : 8,
            font: { size: isMobile ? 10 : 11 },
          },
        },
        title: { display: false },
        tooltip: {
          callbacks: {
            label: function (context) {
              return `${context.dataset.label}: ${formatCurrency(context.parsed.y ?? 0)}`;
            },
            ...buildJournalTooltipCallback(chartMonths, journalEntries),
          },
        },
      },
      scales: {
        y: {
          beginAtZero: false,
          ticks: {
            callback: function (value) {
              return formatCurrency(Number(value));
            },
          },
          grid: {
            color: function (context) {
              if (context.tick.value === 0) return "#374151";
              return "#e5e7eb";
            },
            lineWidth: function (context) {
              if (context.tick.value === 0) return 3;
              return 1;
            },
          },
        },
      },
      interaction: { intersect: false, mode: "index" },
    }),
    [isMobile, chartMonths, journalEntries],
  );

  const activeLoading = viewMode === "comparison" ? isComparisonLoading : isLoading;
  const activeError = viewMode === "comparison" ? comparisonError : error;

  if (activeLoading) {
    return (
      <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-2 text-gray-600 dark:text-graphite-muted">Načítám finanční data...</span>
        </div>
      </div>
    );
  }

  if (activeError) {
    return (
      <div className="w-full max-w-none px-4 sm:px-6 lg:px-8">
        <div className="mb-8 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-900/50 rounded-lg">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
            <h3 className="text-red-800 dark:text-red-300 font-medium">
              Chyba při načítání finančních dat
            </h3>
          </div>
          <p className="mt-1 text-red-700 dark:text-red-300 text-sm">
            {activeError.message || "Neznámá chyba"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col w-full"
      style={{ height: PAGE_CONTAINER_HEIGHT }}
    >
      <div className="flex-shrink-0 mb-3">
        <h1 className="text-lg font-semibold text-gray-900 dark:text-graphite-text">Finanční přehled</h1>
        <p className="mt-1 text-gray-600 dark:text-graphite-muted">
          Přehled příjmů, nákladů a celkové bilance firmy
        </p>
      </div>

      <div className="flex-1 overflow-auto">
        <FinancialFilters
          viewMode={viewMode}
          comparisonYears={comparisonYears}
          comparisonMetrics={comparisonMetrics}
          comparisonRolling={comparisonRolling}
          selectedPeriod={selectedPeriod}
          includeStockData={includeStockData}
          includeCurrentMonth={includeCurrentMonth}
          excludedDepartments={excludedDepartments}
          departments={departments}
          isRefetching={isRefetching}
          onViewModeChange={setViewMode}
          onComparisonYearsChange={setComparisonYears}
          onComparisonMetricsChange={setComparisonMetrics}
          onComparisonRollingChange={setComparisonRolling}
          onPeriodChange={setSelectedPeriod}
          onIncludeStockDataChange={setIncludeStockData}
          onIncludeCurrentMonthChange={setIncludeCurrentMonth}
          onExcludedDepartmentsChange={setExcludedDepartments}
        />

        {viewMode === "comparison" && comparisonData && (
          <>
            {/* Per-year YTD summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              {comparisonData.series.map((s) => (
                <div
                  key={s.year}
                  className="bg-white dark:bg-graphite-surface overflow-hidden shadow dark:shadow-soft-dark rounded-lg"
                >
                  <div className="p-3">
                    <div className="text-xs font-semibold text-gray-700 dark:text-graphite-text mb-1">
                      {s.year} (YTD)
                    </div>
                    <dl className="space-y-0.5">
                      {orderMetrics(comparisonMetrics).map((metric) => {
                        const ytdValue = getYtdForMetric(s, metric);
                        return (
                          <div key={metric} className="flex justify-between gap-2">
                            <dt className="text-xs text-gray-500 dark:text-graphite-muted truncate">
                              {COMPARISON_METRIC_LABELS[metric]}
                            </dt>
                            <dd
                              className={`text-xs font-medium whitespace-nowrap ${
                                ytdValue >= 0
                                  ? "text-emerald-600 dark:text-emerald-400"
                                  : "text-red-600 dark:text-red-400"
                              }`}
                            >
                              {formatCurrency(ytdValue)}
                            </dd>
                          </div>
                        );
                      })}
                    </dl>
                  </div>
                </div>
              ))}
            </div>

            <FinancialComparisonChart
              series={comparisonData.series}
              metrics={comparisonMetrics}
              title="Meziroční srovnání"
              axisMode={comparisonRolling ? "rolling" : "calendar"}
              currentMonth={comparisonData.metadata.partialMonth}
            />

            <div className="bg-white dark:bg-graphite-surface shadow dark:shadow-soft-dark sm:rounded-md mb-8">
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-graphite-border">
                <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-graphite-text">
                  Měsíční srovnání
                </h3>
              </div>
              <FinancialComparisonTable
                series={comparisonData.series}
                metrics={comparisonMetrics}
                axisMode={comparisonRolling ? "rolling" : "calendar"}
                currentMonth={comparisonData.metadata.partialMonth}
              />
            </div>
          </>
        )}

        {/* Summary Cards */}
        {viewMode === "timeline" && data?.summary && (
          <FinancialSummaryCards
            summary={data.summary}
            includeStockData={includeStockData}
          />
        )}

        {/* Chart */}
        {viewMode === "timeline" && chartData && (
          <FinancialChart
            chartData={chartData}
            chartOptions={chartOptions}
            title={`Finanční přehled - ${getPeriodLabel(selectedPeriod)}${includeStockData ? " (včetně skladu)" : ""}`}
          />
        )}

        {/* Journal entries */}
        {viewMode === "timeline" && chartData && (
          <FinancialJournalPanel
            entries={journalEntries}
            isLoading={journalQuery.isLoading}
            isError={journalQuery.isError}
          />
        )}

        {/* Monthly data */}
        {viewMode === "timeline" && data?.data && (
          <>
            {isMobile ? (
              <div className="mb-8">
                <button
                  type="button"
                  onClick={() => setIsDataExpanded((prev) => !prev)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-graphite-surface shadow dark:shadow-soft-dark sm:rounded-md text-left"
                >
                  <span className="text-base font-medium text-gray-900 dark:text-graphite-text">
                    Měsíční data ({data.data.length})
                  </span>
                  {isDataExpanded ? (
                    <ChevronUp className="h-5 w-5 text-gray-500 dark:text-graphite-muted" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-500 dark:text-graphite-muted" />
                  )}
                </button>
                {isDataExpanded && (
                  <div className="mt-2">
                    <FinancialDataCards
                      data={data.data}
                      includeStockData={includeStockData}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white dark:bg-graphite-surface shadow dark:shadow-soft-dark sm:rounded-md mb-8">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200 dark:border-graphite-border">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-graphite-text">
                    Měsíční data
                  </h3>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500 dark:text-graphite-muted">
                    Detailní rozpis příjmů, nákladů a bilance po jednotlivých
                    měsících{includeStockData ? " (včetně skladových dat)" : ""}
                  </p>
                </div>
                <FinancialDataTable
                  data={data.data}
                  includeStockData={includeStockData}
                />
              </div>
            )}
          </>
        )}

        {/* Empty state */}
        {viewMode === "timeline" && data?.data && data.data.length === 0 && (
          <div className="text-center py-12">
            <DollarSign className="mx-auto h-12 w-12 text-gray-400 dark:text-graphite-faint" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-graphite-text">
              Žádná finanční data
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-graphite-muted">
              Pro vybrané období nejsou k dispozici žádná finanční data.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FinancialOverview;
